/**
 * Accent colour: the user's chosen replacement for LiftX's default red brand
 * accent, applied everywhere `tokens.css`'s `--accent*` family is used.
 *
 * Pure logic only — no React, no storage, no DOM — matching `appearance.ts`'s
 * own split between "what the choice is" and "how it's applied/persisted"
 * (`applyAccentColor.ts`, `ThemeProvider.tsx`).
 */

export const ACCENT_COLOURS = ['red', 'blue', 'green', 'orange', 'cyan', 'yellow', 'pink'] as const;
export type AccentColor = (typeof ACCENT_COLOURS)[number];

/** Red is LiftX's original, only-ever brand colour — existing users and a
 *  fresh install must both look exactly as they always have until someone
 *  explicitly picks something else. */
export const DEFAULT_ACCENT_COLOR: AccentColor = 'red';

export function isAccentColor(value: unknown): value is AccentColor {
  return typeof value === 'string' && (ACCENT_COLOURS as readonly string[]).includes(value);
}

export const ACCENT_COLOUR_LABELS: Record<AccentColor, string> = {
  red: 'Red',
  blue: 'Blue',
  green: 'Green',
  orange: 'Orange',
  cyan: 'Cyan',
  yellow: 'Yellow',
  pink: 'Pink',
};

/**
 * The swatch itself always shows each colour's light-theme `--accent` value
 * (`tokens.css`), regardless of which appearance is active — the picker's job
 * is to let someone compare all seven at a glance, not to preview how muted
 * dark mode makes them.
 */
export const ACCENT_COLOUR_SWATCHES: Record<AccentColor, string> = {
  red: '#c41e17',
  blue: '#1c5fc4',
  green: '#1a7a3c',
  orange: '#b4550f',
  cyan: '#0f7a8c',
  yellow: '#8a6d0a',
  pink: '#c21e6d',
};
